/**
 * Application Settings — Import limits.
 *
 * Edits import-config/limits.json in the data-importer's bucket so an operator
 * never has to open S3 or hand-upload a file.
 *
 * TWO UNITS ON ONE SCREEN, KEPT VISUALLY APART because they are not the same
 * kind of number and confusing them is the mistake this layout exists to
 * prevent:
 *
 *   Rows per file    per entity, per import, RESETS every import. Enforced by
 *                    the importer — an over-limit file is rejected with 422.
 *   Items per tenant CUMULATIVE and permanent. NOT enforced server-side; these
 *                    records are written straight to AppSync from the browser.
 *                    The section says so in as many words.
 *
 * Concurrency: the config is read with its ETag and written back with If-Match.
 * A conflict does NOT overwrite — it shows what the other person changed and
 * makes the operator choose.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '../services/importLimitsService';
import {
  DEFAULT_KEY, LimitsConfig, LimitsDocument, SELECTION_KINDS, Tier,
  describeTier, hasSplitRowLimits, isLegacyShape, isValidLimit, rowLimitsOf,
  selectionLimitsOf, tierStatus, toEditableShape,
} from '../models/importLimits';

const inp: React.CSSProperties = {
  width: 96, padding: '5px 8px', border: '1px solid #cdd6e0', borderRadius: 6, fontSize: 13,
};
const inpBad: React.CSSProperties = { ...inp, borderColor: '#c24a56', background: '#fdf3f3' };
const wide: React.CSSProperties = { ...inp, width: 340 };
const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase',
  letterSpacing: '.04em', color: '#6b7684', borderBottom: '1px solid #e3e9ef', fontWeight: 600,
};
const td: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid #eef2f6', fontSize: 13 };
const note: React.CSSProperties = {
  fontSize: 12, lineHeight: 1.55, color: '#6b7684', margin: '4px 0 14px',
};
const warn: React.CSSProperties = {
  ...note, background: '#fffaf0', border: '1px solid #f0d9a8', borderRadius: 8,
  padding: '10px 12px', color: '#7a5b1e',
};

/**
 * Entities a tenant can actually upload a file of, TODAY, in the wizard.
 *
 * Not the same as what the importer is capable of creating. `endpoint_registry`
 * carries a create mutation for fourteen types — country, currency, customs
 * agent, delivery priority, unit type, agent and so on — but no screen ever
 * sends them, so a limit on those rows is a control over nothing. Showing them
 * invited an operator to set a number, watch it have no effect, and reasonably
 * conclude the limits do not work.
 *
 * Six come from dedicated wizard steps, which each call checkRowLimit with the
 * key below; wizard_shipping_port comes from the "Import everything" step's
 * entityCards. If a step is added, add its key here — the screen is hardcoded
 * so it still renders when the importer is unreachable.
 *
 * wizard_chart_of_accounts is not an ENDPOINTS entry — the CoA import has its
 * own routes and its own commit path — but it is enforced by the same
 * enforce_import_limit() on /coa/map/jobs and /coa/import/jobs, so it belongs
 * on this screen like any other.
 */
const ENTITIES = [
  'wizard_branch', 'wizard_chart_of_accounts', 'wizard_customer',
  'wizard_department', 'wizard_package_type', 'wizard_particular',
  'wizard_shipping_port', 'wizard_vendor',
];

/** Readable names for the entity keys — the wording the wizard's own steps use,
 *  so an operator setting "Commodities" can find the step that enforces it.
 *  Note `wizard_shipping_port` renders as "Ports" here AND appears in the
 *  section below as a selection cap: those are different units on the same
 *  noun, which the note under that table spells out. */
const ENTITY_LABELS: Record<string, string> = {
  wizard_branch:            'Locations',
  wizard_chart_of_accounts: 'Chart of accounts',
  wizard_customer:          'Customers',
  wizard_department:        'Departments',
  wizard_package_type:      'Package types',
  wizard_particular:        'Commodities',
  wizard_shipping_port:     'Ports',
  wizard_vendor:            'Vendors',
};

const entityLabel = (key: string) =>
  ENTITY_LABELS[key] ?? key.replace('wizard_', '').replace(/_/g, ' ');

const TIERS: Tier[] = ['purchased', 'trial'];

/** Tenants shown at once. The override list is expected to reach four figures,
 *  and each expanded tenant is ~14 inputs plus a detection request. */
const PAGE_SIZE = 20;

/** Deep clone that survives the structuredClone-less older targets. */
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

export const ImportLimits: React.FC = () => {
  const [doc, setDoc] = useState<LimitsDocument | null>(null);
  const [draft, setDraft] = useState<LimitsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [conflict, setConflict] = useState<{ current: LimitsDocument; diff: string[] } | null>(null);
  const [tenantGuid, setTenantGuid] = useState('');
  /** guid -> what the server DETECTED, from /import/meta's tier_detail. Only
   *  the server can read isPurchase, so the UI asks rather than guessing. */
  const [detected, setDetected] = useState<Record<string, { tier?: string; detected?: boolean }>>({});

  // Per-tenant list controls. This list is expected to reach four figures, so
  // it is searched and paged rather than rendered whole, and only the tenant
  // being edited renders its inputs.
  const [tenantQuery, setTenantQuery] = useState('');
  const [page, setPage] = useState(0);
  const [openGuid, setOpenGuid] = useState<string | null>(null);

  const flash = (m: string) => { setErr(''); setMsg(m); window.setTimeout(() => setMsg(''), 3000); };

  const load = useCallback(() => {
    setLoading(true); setErr(''); setConflict(null);
    api.loadLimits()
      // Normalise to the v2 shape on load so the reader and the writer agree on
      // which block is live. `doc` keeps the server's document verbatim.
      .then(d => { setDoc(d); setDraft(toEditableShape(d.config)); })
      .catch(e => setErr(String(e?.message || e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const rows = draft ? rowLimitsOf(draft) : { tiers: {}, tenants: {} };
  const sels = draft ? selectionLimitsOf(draft) : { tiers: {}, tenants: {} };

  const allTenants = useMemo(() => Array.from(new Set([
    ...Object.keys(rows.tenants || {}),
    ...Object.keys(sels.tenants || {}),
  ])).sort(), [rows, sels]);

  const matchedTenants = useMemo(() => {
    const q = tenantQuery.trim().toLowerCase();
    return q ? allTenants.filter(g => g.toLowerCase().includes(q)) : allTenants;
  }, [allTenants, tenantQuery]);

  const pageCount = Math.max(1, Math.ceil(matchedTenants.length / PAGE_SIZE));
  const pageIndex = Math.min(page, pageCount - 1);
  const pageTenants = useMemo(
    () => matchedTenants.slice(pageIndex * PAGE_SIZE, pageIndex * PAGE_SIZE + PAGE_SIZE),
    [matchedTenants, pageIndex],
  );

  // Detection is one request per tenant, so only ask for the page on screen.
  // Asking for the whole list would be a request per tenant on every load.
  useEffect(() => {
    pageTenants.filter(g => !(g in detected)).forEach(g => {
      api.detectTier(g)
        .then(d => setDetected(prev => ({ ...prev, [g]: d })))
        // A failed probe leaves the row saying "detection unavailable", which
        // is the honest thing to show and is also what the importer would fall
        // back to.
        .catch(() => setDetected(prev => ({ ...prev, [g]: { detected: false } })));
    });
  }, [pageTenants, detected]);

  /** The server's document in the shape the draft is edited in, so opening a
   *  v1 file does not read as an unsaved edit. */
  const baseline = useMemo(() => (doc ? toEditableShape(doc.config) : null), [doc]);

  const dirty = useMemo(
    () => !!baseline && !!draft && JSON.stringify(baseline) !== JSON.stringify(draft),
    [baseline, draft],
  );

  // ── mutation helpers ──────────────────────────────────────────────────────
  // Every edit goes through setIn so the v1/v2 shape question is answered in
  // exactly one place: edits always land in the v2 blocks, which is also how a
  // legacy file gets migrated the first time it is saved.

  const setIn = (path: string[], value: number | string | undefined) => {
    setDraft(prev => {
      if (!prev) return prev;
      const next = clone(prev);
      let node: any = next;
      for (const seg of path.slice(0, -1)) {
        if (node[seg] === undefined || node[seg] === null || typeof node[seg] !== 'object') node[seg] = {};
        node = node[seg];
      }
      const leaf = path[path.length - 1];
      if (value === '' || value === undefined) delete node[leaf];
      else node[leaf] = value;
      return next;
    });
  };

  /** Row-limit edits always write to import_limits, even for a v1 document. */
  const setRowTier = (tier: Tier, key: string, v: string) =>
    setIn(['import_limits', 'tiers', tier, key], v === '' ? '' : Number(v));
  const setRowTenant = (guid: string, key: string, v: string) =>
    setIn(['import_limits', 'tenants', guid, key], v === '' ? '' : Number(v));
  const setSelTier = (tier: Tier, key: string, v: string) =>
    setIn(['selection_limits', 'tiers', tier, key], v === '' ? '' : Number(v));
  const setSelTenant = (guid: string, key: string, v: string) =>
    setIn(['selection_limits', 'tenants', guid, key], v === '' ? '' : Number(v));

  const setTier = (guid: string, tier: Tier | '') =>
    setIn(['import_limits', 'tenants', guid, 'tier'], tier === '' ? undefined : tier);

  // ── validation ────────────────────────────────────────────────────────────
  const invalid = useMemo(() => {
    if (!draft) return [];
    const bad: string[] = [];
    const scan = (obj: any, label: string) => {
      Object.keys(obj || {}).forEach(k => {
        if (k === 'tier' || k === '_comment' || k === '_readme') return;
        if (!isValidLimit(obj[k])) bad.push(`${label} → ${k}`);
      });
    };
    TIERS.forEach(t => {
      scan(rows.tiers?.[t], `Rows / ${t}`);
      scan(sels.tiers?.[t], `Items / ${t}`);
    });
    Object.keys(rows.tenants || {}).forEach(g => scan(rows.tenants[g], `Rows / ${g.slice(0, 8)}…`));
    Object.keys(sels.tenants || {}).forEach(g => scan(sels.tenants[g], `Items / ${g.slice(0, 8)}…`));
    return bad;
  }, [draft, rows, sels]);

  const save = () => {
    if (!draft || !doc || invalid.length) return;
    setSaving(true); setErr(''); setConflict(null);
    // Stamp the shape as v2 — the importer reads both, but a file that has been
    // through this editor should not still look like v1 to the next reader.
    const body = clone(draft);
    body.schema_version = 2;
    api.saveLimits(body, doc.etag)
      .then(saved => { setDoc(saved); setDraft(toEditableShape(saved.config)); flash('Saved. Live on every instance within 5 minutes.'); })
      .catch(e => {
        if (e instanceof api.ImportLimitsConflict) {
          // Normalise theirs too — diffing a v1 against a v2 document would
          // report every key as changed.
          setConflict({
            current: e.current,
            diff: api.describeDifferences(draft, toEditableShape(e.current.config)),
          });
        } else {
          setErr(String(e?.message || e));
        }
      })
      .finally(() => setSaving(false));
  };

  /** How many values a tenant actually overrides — shown on the collapsed row
   *  so the list is scannable without opening anything. */
  const overrideCount = (guid: string) => {
    const skip = (k: string) => k === 'tier' || k === '_comment' || k === '_readme';
    return Object.keys(rows.tenants?.[guid] || {}).filter(k => !skip(k)).length
         + Object.keys(sels.tenants?.[guid] || {}).filter(k => !skip(k)).length;
  };

  if (loading) return <div className="ov-page"><div style={note}>Loading configuration…</div></div>;

  return (
    <div className="ov-page">
      <div className="ov-header">
        <div>
          <div className="ov-header__title">Application settings — import limits</div>
          <div className="ov-header__sub">
            Edits <code>import-config/limits.json</code> directly. Internal operators only.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-small btn-small--ghost" onClick={load} disabled={saving}>Reload</button>
          <button
            className="btn-small btn-small--primary"
            onClick={save}
            disabled={!dirty || saving || invalid.length > 0}
            title={invalid.length ? 'Fix the highlighted values first' : undefined}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {msg && <div style={{ ...note, color: '#0f9d6b' }}>{msg}</div>}
      {err && <div style={{ ...warn, borderColor: '#f3c3c6', background: '#fdf3f3', color: '#8a2f38' }}>{err}</div>}

      {invalid.length > 0 && (
        <div style={warn}>
          <strong>These values will not apply.</strong> A limit must be a whole number above zero;
          the importer ignores anything else and falls through to the next level.
          <div style={{ marginTop: 6 }}>{invalid.join(' · ')}</div>
        </div>
      )}

      {/* Both read doc.config, the server's shape — the draft is already
          migrated, so checking it would hide the notice. */}
      {doc && isLegacyShape(doc.config) && (
        <div style={warn}>
          This file uses the original layout, with row limits at the top level. It has been
          migrated to <code>import_limits</code> for editing and will be saved that way.
          The importer reads both, so nothing breaks either way.
        </div>
      )}

      {doc && hasSplitRowLimits(doc.config) && (
        <div style={{ ...warn, borderColor: '#c24a56', background: '#fdf3f3', color: '#8a2f38' }}>
          <strong>This file holds two layouts and the importer is only reading one.</strong>{' '}
          Row limits at the top level are being ignored, so the entities they covered are
          running on built-in fallbacks. They have been merged back in below.
          Review the values and save to put them back into effect.
        </div>
      )}

      {conflict && (
        <div style={{ ...warn, borderColor: '#d9a45f' }}>
          <strong>Someone else saved while you were editing.</strong> Nothing has been overwritten.
          {conflict.current.lastModified && <> Their version was written {conflict.current.lastModified}.</>}
          <div style={{ marginTop: 8, fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5 }}>
            {conflict.diff.length
              ? conflict.diff.map(d => <div key={d}>{d}</div>)
              : <div>The differences are outside the limit values.</div>}
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button className="btn-small btn-small--ghost" onClick={load}>
              Discard mine and load theirs
            </button>
            <button
              className="btn-small btn-small--primary"
              onClick={() => {
                // Re-base onto their version, keeping this operator's edits, so
                // the next save carries a current ETag. Still their call.
                setDoc(conflict.current);
                setConflict(null);
                flash('Re-based on their version — review, then save again.');
              }}
            >
              Keep mine and re-base
            </button>
          </div>
        </div>
      )}

      {/* ── ROWS PER FILE ─────────────────────────────────────────────────── */}
      <div className="ov-section">
        <div className="ov-section__head">
          <div className="ov-section__title">Rows in one uploaded file</div>
        </div>
        <div style={note}>
          When a tenant uploads a spreadsheet, this is the most rows it may contain for that
          record type. It applies to <strong>one file at a time</strong> and resets on the next
          upload — it is not a running total, so 5000 customers does not mean the account may
          only ever have 5000 customers.
          <br />
          <strong>Enforced by the importer.</strong> An over-limit file is rejected before
          anything is written, and the user is told the record type, the limit and their actual
          row count.
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Record type in the file</th>
              {TIERS.map(t => <th key={t} style={th}>{t}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...td, fontWeight: 600 }}>
                Everything else <span style={{ color: '#6b7684', fontWeight: 400 }}>(_default)</span>
              </td>
              {TIERS.map(t => (
                <td key={t} style={td}>
                  <input
                    style={isValidLimit(rows.tiers?.[t]?.[DEFAULT_KEY]) ? inp : inpBad}
                    value={String(rows.tiers?.[t]?.[DEFAULT_KEY] ?? '')}
                    onChange={e => setRowTier(t, DEFAULT_KEY, e.target.value)}
                    placeholder="—"
                  />
                </td>
              ))}
            </tr>
            {ENTITIES.map(ent => (
              <tr key={ent}>
                <td style={td}>{entityLabel(ent)}</td>
                {TIERS.map(t => (
                  <td key={t} style={td}>
                    <input
                      style={isValidLimit(rows.tiers?.[t]?.[ent]) ? inp : inpBad}
                      value={String(rows.tiers?.[t]?.[ent] ?? '')}
                      onChange={e => setRowTier(t, ent, e.target.value)}
                      placeholder="—"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── ITEMS PER TENANT ──────────────────────────────────────────────── */}
      <div className="ov-section">
        <div className="ov-section__head">
          <div className="ov-section__title">How many a tenant can add</div>
        </div>
        <div style={note}>
          Nothing to do with uploading a file. During setup a tenant ticks the currencies,
          ports and languages they work with — these are the caps on how many they may tick.
          A cap of 3 on languages means the account can have three languages, ever.
          Leave a box empty and the default in the last column applies.
        </div>
        <div style={note}>
          <strong>“Ports” appears in both tables and means two different things.</strong>{' '}
          Above, it is how many rows a spreadsheet of ports may have in one upload. Here, it is
          how many ports the account may end up holding in total. A tenant can upload a
          5000-row port file and still only be allowed to keep 20 of them.
        </div>
        <div style={warn}>
          <strong>These are a guide in the browser, not an enforced limit.</strong> Currencies,
          ports and languages are saved directly from the setup screens and never reach the
          importer, so there is nothing on the server to check them. They keep the screens
          consistent; they do not stop a determined caller.
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>What the tenant is adding</th>
              {TIERS.map(t => <th key={t} style={th}>{t}</th>)}
              <th style={th}>Default if left empty</th>
            </tr>
          </thead>
          <tbody>
            {SELECTION_KINDS.map(({ kind, label, hint, replaces }) => (
              <tr key={kind}>
                <td style={td}>
                  {label}{' '}
                  <span style={{ color: '#6b7684', fontSize: 12 }}>{hint}</span>
                </td>
                {TIERS.map(t => (
                  <td key={t} style={td}>
                    <input
                      style={isValidLimit(sels.tiers?.[t]?.[kind]) ? inp : inpBad}
                      value={String(sels.tiers?.[t]?.[kind] ?? '')}
                      onChange={e => setSelTier(t, kind, e.target.value)}
                      placeholder="—"
                    />
                  </td>
                ))}
                <td style={{ ...td, color: '#6b7684', fontSize: 12 }}>{replaces}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── PER-TENANT OVERRIDES + TIER ───────────────────────────────────── */}
      <div className="ov-section">
        <div className="ov-section__head">
          <div className="ov-section__title">Per-tenant overrides</div>
        </div>
        <div style={note}>
          Most specific wins: a tenant entry beats its <code>_default</code>, which beats the
          tier, which beats the built-in. Leave a box empty to fall through.
        </div>
        <div style={note}>
          <strong>Tier is detected, not chosen.</strong> The importer reads{' '}
          <code>isPurchase</code> from the tenant record server-side — never from the
          request, because a limit is a commercial control and a user must not be able to
          raise their own by editing a token. Setting the dropdown <em>pins</em> a tier on
          top of that, which is for the cases where payment state is right but policy
          differs: a payment made for another product, a chargeback under investigation, a
          partner billed outside the gateway, or a pilot that needs purchased limits without
          having paid. Leave it on “Not set” unless you mean it.
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '0 0 10px', flexWrap: 'wrap' }}>
          <input
            style={wide}
            value={tenantGuid}
            onChange={e => setTenantGuid(e.target.value.trim())}
            placeholder="Tenant GUID"
          />
          <button
            className="btn-small btn-small--ghost"
            disabled={!tenantGuid || allTenants.includes(tenantGuid)}
            onClick={() => {
              setIn(['import_limits', 'tenants', tenantGuid, 'tier'], 'purchased');
              setTenantQuery(tenantGuid);
              setOpenGuid(tenantGuid);
              setPage(0);
              setTenantGuid('');
            }}
          >
            Add tenant
          </button>
        </div>

        {/* Search + paging. Only tenants WITH an override appear here; a tenant
            with no entry needs no row, and listing every tenant in the estate
            would make the common case (a handful of exceptions) unusable. */}
        {allTenants.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '0 0 12px', flexWrap: 'wrap' }}>
            <input
              style={wide}
              value={tenantQuery}
              onChange={e => { setTenantQuery(e.target.value); setPage(0); }}
              placeholder="Search these overrides by GUID"
            />
            <span style={{ fontSize: 12, color: '#6b7684' }}>
              {matchedTenants.length === allTenants.length
                ? `${allTenants.length} with overrides`
                : `${matchedTenants.length} of ${allTenants.length}`}
            </span>
            {pageCount > 1 && (
              <span style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto' }}>
                <button className="btn-small btn-small--ghost"
                        disabled={pageIndex === 0}
                        onClick={() => setPage(p => Math.max(0, p - 1))}>Previous</button>
                <span style={{ fontSize: 12, color: '#6b7684' }}>
                  Page {pageIndex + 1} of {pageCount}
                </span>
                <button className="btn-small btn-small--ghost"
                        disabled={pageIndex >= pageCount - 1}
                        onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}>Next</button>
              </span>
            )}
          </div>
        )}

        {allTenants.length === 0 && <div style={note}>No per-tenant overrides yet.</div>}
        {allTenants.length > 0 && matchedTenants.length === 0 && (
          <div style={note}>No override matches “{tenantQuery}”.</div>
        )}

        {pageTenants.map(guid => {
          const st = tierStatus(draft ?? {}, guid, detected[guid]);
          const open = openGuid === guid;
          const n = overrideCount(guid);
          return (
            <div key={guid} style={{ border: '1px solid #e3e9ef', borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <button
                  className="btn-small btn-small--ghost"
                  style={{ minWidth: 72 }}
                  onClick={() => setOpenGuid(open ? null : guid)}
                >
                  {open ? 'Close' : 'Edit'}
                </button>

                <code style={{ fontSize: 12 }}>{guid}</code>

                <span style={{ fontSize: 12, color: '#6b7684' }}>
                  {n === 0 ? 'tier only' : `${n} limit${n === 1 ? '' : 's'} overridden`}
                </span>

                <label style={{ fontSize: 12, color: '#6b7684' }}>
                  Tier{' '}
                  <select
                    value={(rows.tenants?.[guid]?.tier as string) ?? ''}
                    onChange={e => setTier(guid, e.target.value as Tier | '')}
                    style={{ ...inp, width: 150 }}
                  >
                    <option value="">Not set — use detected</option>
                    <option value="purchased">purchased</option>
                    <option value="trial">trial</option>
                  </select>
                </label>

                {/* Detected vs pinned, never a bare word. "purchased because
                    they paid", "purchased because an operator pinned it" and
                    "purchased because the lookup failed" are three different
                    facts and the operator has to be able to tell them apart. */}
                <span style={{
                  fontSize: 12,
                  color: st.pinned ? '#7a5b1e' : (st.detected ? '#0f9d6b' : '#8a2f38'),
                }}>
                  In effect: <strong>{describeTier(st)}</strong>
                </span>
              </div>

              {/* Inputs render only for the tenant being edited. Rendering all
                  of them is ~14 controls per tenant, which does not survive a
                  four-figure list. */}
              {open && (
              <div style={{ display: 'flex', gap: 24, marginTop: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ ...th, borderBottom: 'none', padding: '0 0 6px' }}>Rows per file</div>
                  {[DEFAULT_KEY, ...ENTITIES].map(k => (
                    <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 12, width: 150, color: '#4a5563' }}>
                        {k === DEFAULT_KEY ? 'Everything else' : entityLabel(k)}
                      </span>
                      <input
                        style={isValidLimit(rows.tenants?.[guid]?.[k]) ? inp : inpBad}
                        value={String(rows.tenants?.[guid]?.[k] ?? '')}
                        onChange={e => setRowTenant(guid, k, e.target.value)}
                        placeholder="—"
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <div style={{ ...th, borderBottom: 'none', padding: '0 0 6px' }}>How many they can add</div>
                  {SELECTION_KINDS.map(({ kind, label }) => (
                    <div key={kind} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 12, width: 150, color: '#4a5563' }}>{label}</span>
                      <input
                        style={isValidLimit(sels.tenants?.[guid]?.[kind]) ? inp : inpBad}
                        value={String(sels.tenants?.[guid]?.[kind] ?? '')}
                        onChange={e => setSelTenant(guid, kind, e.target.value)}
                        placeholder="—"
                      />
                    </div>
                  ))}
                </div>
              </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={note}>
        Changes are picked up by each importer instance within its 5-minute cache window,
        so allow up to five minutes for every instance to agree.
      </div>
    </div>
  );
};
